/**
 * Patients mock adapter.
 *
 * Mirrors the `patientsApi` surface in `../patients.ts` so the
 * `MOCKS_ENABLED ? mockPatients : realPatientsApi` swap is a no-op for
 * callers. Holds an in-memory store seeded with synthetic VN patients so
 * `/patients` (list, search, detail, allergies, conditions, alerts, tags)
 * is fully click-through without a backend or tenant header.
 */
import { delay, seededRandom } from "./registry";
import type {
  AlertCreateRequest,
  AlertResponse,
  AllergyCreateRequest,
  AllergyOverrideReason,
  AllergyResponse,
  AllergyUpdateRequest,
  ConditionCreateRequest,
  ConditionResponse,
  ConditionUpdateRequest,
  PageResponse,
  PatientCreateRequest,
  PatientCreateResponse,
  PatientDuplicateWarning,
  PatientGender,
  PatientListFilter,
  PatientIdentifierResponse,
  PatientResponse,
  PatientSearchResult,
  PatientStatus,
  PatientSummaryResponse,
  PatientUpdateRequest,
  TagsResponse,
} from "../../types/patient";

/* ------------------------------------------------------------------ */
/* Seed data                                                          */
/* ------------------------------------------------------------------ */

// [code, fullName, gender, dob, phone, provinceCode, status]
const SEED: [
  string,
  string,
  PatientGender,
  string,
  string,
  string,
  PatientStatus,
][] = [
  ["BN-AB12CD34", "Đỗ Văn Giang", "M", "1989-03-15", "+84967369125", "01", "ACTIVE"],
  ["BN-EF56GH78", "Nguyễn Thị Lan", "F", "1995-07-22", "+84934567890", "79", "ACTIVE"],
  ["BN-JK90LM12", "Trần Văn Hùng", "M", "1978-11-04", "+84912345678", "48", "ACTIVE"],
  ["BN-NP34QR56", "Phạm Thị Mai", "F", "2001-05-10", "+84988776655", "79", "ACTIVE"],
  ["BN-ST78UV90", "Hoàng Văn Nam", "M", "1965-08-18", "+84909123456", "31", "ACTIVE"],
  ["BN-WX12YZ34", "Lê Thị Hoa", "F", "1992-02-28", "+84945678123", "01", "ACTIVE"],
  ["BN-AB56CD78", "Vũ Đình Tâm", "M", "1986-12-03", "+84978234567", "92", "ACTIVE"],
  ["BN-EF90GH12", "Bùi Thị Phượng", "F", "1973-06-14", "+84923456789", "79", "ARCHIVED"],
  ["BN-HJ34KL56", "Đặng Quốc Anh", "M", "1999-09-09", "+84961112223", "01", "ACTIVE"],
  ["BN-MN78PQ90", "Ngô Thị Thu", "F", "1984-04-19", "+84935557788", "48", "ACTIVE"],
  ["BN-RS12TU34", "Dương Văn Lợi", "M", "1958-01-27", "+84909998877", "79", "DECEASED"],
  ["BN-VW56XY78", "Tạ Thị Ngọc", "F", "2010-10-02", "+84944443322", "31", "ACTIVE"],
  ["BN-ZA90BC12", "Lý Hoàng Long", "M", "1990-07-30", "+84966677788", "79", "ACTIVE"],
  ["BN-DE34FG56", "Phan Thị Kim", "F", "1981-03-08", "+84933221144", "01", "ACTIVE"],
  ["BN-HI78JK90", "Trịnh Văn Bảo", "M", "1976-11-21", "+84977665544", "92", "ACTIVE"],
  ["BN-LM12NO34", "Võ Thị Hạnh", "F", "1997-12-12", "+84922113355", "48", "ACTIVE"],
  ["BN-PQ56RS78", "Cao Văn Đức", "M", "1969-05-05", "+84909112233", "79", "ARCHIVED"],
  ["BN-TU90VW12", "Hồ Thị Yến", "F", "2003-08-25", "+84944556677", "01", "ACTIVE"],
  ["BN-XY34ZA56", "Mai Văn Sơn", "M", "1988-02-17", "+84966554433", "31", "ACTIVE"],
  ["BN-BC78DE90", "Đinh Thị Quỳnh", "F", "1994-06-06", "+84933778899", "79", "ACTIVE"],
];

function ageFrom(dob: string): number {
  const d = new Date(dob);
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a--;
  return a;
}

const rnd = seededRandom(42);

function buildPatients(): PatientResponse[] {
  const base = Date.now();
  return SEED.map(([code, fullName, gender, dob, phone, province, status], i) => {
    const updatedAt = new Date(base - i * 3_600_000 - Math.floor(rnd() * 86_400_000));
    const createdAt = new Date(updatedAt.getTime() - 30 * 86_400_000);
    return {
      id: `pat-${code.slice(3).toLowerCase()}`,
      patientCode: code,
      fullName,
      gender,
      dateOfBirth: dob,
      dobPrecision: "DAY",
      age: ageFrom(dob),
      phoneE164: phone,
      email: null,
      addressLine: `Số ${10 + i} đường Lê Lợi`,
      wardCode: null,
      provinceCode: province,
      nationality: "VN",
      ethnicityCode: "Kinh",
      occupation: null,
      organization: null,
      bloodType: null,
      heightCm: null,
      weightKg: null,
      notes: null,
      guardianName: null,
      guardianPhone: null,
      guardianRelationship: null,
      primaryBranchId: null,
      primaryDoctorId: null,
      status,
      mergedIntoId: null,
      deceasedAt: status === "DECEASED" ? "2026-04-30" : null,
      createdAt: createdAt.toISOString(),
      updatedAt: updatedAt.toISOString(),
      version: 1,
    } satisfies PatientResponse;
  });
}

const PATIENTS: PatientResponse[] = buildPatients();
const ALLERGIES: Record<string, AllergyResponse[]> = {};
const CONDITIONS: Record<string, ConditionResponse[]> = {};
const ALERTS: Record<string, AlertResponse[]> = {};
const IDENTIFIERS: Record<string, PatientIdentifierResponse[]> = {};
const TAGS: Record<string, string[]> = {};

// Seed clinical sub-records for the first two patients so detail panels
// render with content.
const p0 = PATIENTS[0]!.id;
const p1 = PATIENTS[1]!.id;
const nowISO = new Date().toISOString();
ALLERGIES[p0] = [
  {
    id: "alg-001",
    category: "DRUG",
    substance: "Penicillin",
    substanceCode: null,
    severity: "SEVERE",
    reaction: "Phát ban, khó thở",
    onsetDate: "2018-05-01",
    verifiedAt: nowISO,
    verifiedBy: "BS. Nguyễn Văn A",
    source: "CLINICIAN",
    active: true,
    createdAt: nowISO,
    updatedAt: nowISO,
  },
];
CONDITIONS[p0] = [
  {
    id: "cnd-001",
    icd10Code: "I10",
    nameVi: "Tăng huyết áp vô căn",
    onsetDate: "2020-01-15",
    resolvedDate: null,
    severity: "MODERATE",
    notes: "Đang điều trị ngoại trú",
    active: true,
    createdAt: nowISO,
    updatedAt: nowISO,
  },
];
ALERTS[p0] = [
  {
    id: "alt-001",
    alertType: "ALLERGY",
    severity: "CRITICAL",
    titleVi: "Dị ứng Penicillin nặng",
    descriptionVi: "Tránh nhóm beta-lactam",
    expiresAt: null,
    acknowledgedBy: null,
    acknowledgedAt: null,
    active: true,
    createdAt: nowISO,
  },
];
TAGS[p0] = ["VIP", "Mạn tính"];
TAGS[p1] = ["Thai sản"];

/* ------------------------------------------------------------------ */
/* Filtering                                                          */
/* ------------------------------------------------------------------ */

function toSummary(p: PatientResponse): PatientSummaryResponse {
  return {
    id: p.id,
    patientCode: p.patientCode,
    fullName: p.fullName,
    gender: p.gender,
    dateOfBirth: p.dateOfBirth,
    age: p.age,
    phoneE164: p.phoneE164,
    email: p.email,
    addressLine: p.addressLine,
    provinceCode: p.provinceCode,
    wardCode: p.wardCode,
    guardianName: p.guardianName,
    status: p.status,
    updatedAt: p.updatedAt,
  };
}

function applyFilter(list: PatientResponse[], f: PatientListFilter | undefined) {
  if (!f) return list;
  let out = list;
  if (f.q) {
    const needle = f.q.trim().toLowerCase();
    out = out.filter(
      (p) =>
        p.fullName.toLowerCase().includes(needle) ||
        (p.patientCode ?? "").toLowerCase().includes(needle) ||
        (p.phoneE164 ?? "").includes(needle)
    );
  }
  if (f.status) out = out.filter((p) => p.status === f.status);
  if (f.gender) out = out.filter((p) => p.gender === f.gender);
  if (f.provinceCode) out = out.filter((p) => p.provinceCode === f.provinceCode);
  if (f.ageMin != null) out = out.filter((p) => (p.age ?? 0) >= f.ageMin!);
  if (f.ageMax != null) out = out.filter((p) => (p.age ?? 0) <= f.ageMax!);
  return out;
}

function paginate<T>(rows: T[], page = 0, size = 20): PageResponse<T> {
  const totalElements = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalElements / size));
  const start = page * size;
  return {
    content: rows.slice(start, start + size),
    totalElements,
    totalPages,
    number: page,
    size,
  };
}

function find(id: string): PatientResponse {
  const p = PATIENTS.find((x) => x.id === id);
  if (!p) throw new Error(`Mock: bệnh nhân ${id} không tồn tại`);
  return p;
}

function touch(p: PatientResponse) {
  p.updatedAt = new Date().toISOString();
  p.version += 1;
}

/* ------------------------------------------------------------------ */
/* Adapter                                                            */
/* ------------------------------------------------------------------ */

export const mockPatients = {
  list(
    filter: PatientListFilter | undefined,
    pageable: { page?: number; size?: number; sort?: string }
  ): Promise<PageResponse<PatientSummaryResponse>> {
    const rows = applyFilter(PATIENTS, filter)
      .slice()
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
      .map(toSummary);
    return delay(paginate(rows, pageable.page ?? 0, pageable.size ?? 20));
  },

  search(
    q: string,
    pageable: { page?: number; size?: number }
  ): Promise<PageResponse<PatientSearchResult>> {
    const rows = applyFilter(PATIENTS, { q })
      .map((p) => ({ ...toSummary(p), matchScore: 0.9 }))
      .sort((a, b) => (b.matchScore ?? 0) - (a.matchScore ?? 0));
    return delay(paginate(rows, pageable.page ?? 0, pageable.size ?? 20));
  },

  get(id: string): Promise<PatientResponse> {
    return delay({ ...find(id) });
  },

  create(req: PatientCreateRequest): Promise<PatientCreateResponse> {
    const code = `BN-${Math.random().toString(36).slice(2, 10).toUpperCase()}`;
    const now = new Date().toISOString();
    const patient: PatientResponse = {
      id: `pat-${code.slice(3).toLowerCase()}`,
      patientCode: code,
      fullName: req.fullName,
      gender: req.gender ?? "UNKNOWN",
      dateOfBirth: req.dateOfBirth ?? null,
      dobPrecision: req.dobPrecision ?? "DAY",
      age: req.dateOfBirth ? ageFrom(req.dateOfBirth) : null,
      phoneE164: req.phone ?? null,
      email: req.email ?? null,
      addressLine: req.addressLine ?? null,
      wardCode: req.wardCode ?? null,
      provinceCode: req.provinceCode ?? null,
      nationality: req.nationality ?? "VN",
      ethnicityCode: req.ethnicityCode ?? null,
      occupation: req.occupation ?? null,
      organization: req.organization ?? null,
      bloodType: req.bloodType ?? null,
      heightCm: req.heightCm ?? null,
      weightKg: req.weightKg ?? null,
      notes: req.notes ?? null,
      guardianName: req.guardianName ?? null,
      guardianPhone: req.guardianPhone ?? null,
      guardianRelationship: req.guardianRelationship ?? null,
      primaryBranchId: req.primaryBranchId ?? null,
      primaryDoctorId: req.primaryDoctorId ?? null,
      status: "ACTIVE",
      mergedIntoId: null,
      deceasedAt: null,
      createdAt: now,
      updatedAt: now,
      version: 1,
    };
    PATIENTS.unshift(patient);
    return delay({ patient, warning: null });
  },

  checkDuplicates(_probe: {
    fullName: string;
    phone?: string | null;
    dateOfBirth?: string | null;
    cccd?: string | null;
  }): Promise<PatientDuplicateWarning | null> {
    return delay(null);
  },

  update(id: string, req: PatientUpdateRequest): Promise<PatientResponse> {
    const p = find(id);
    Object.assign(p, {
      fullName: req.fullName ?? p.fullName,
      gender: req.gender ?? p.gender,
      dateOfBirth: req.dateOfBirth ?? p.dateOfBirth,
      phoneE164: req.phone ?? p.phoneE164,
      email: req.email ?? p.email,
      addressLine: req.addressLine ?? p.addressLine,
      provinceCode: req.provinceCode ?? p.provinceCode,
      notes: req.notes ?? p.notes,
    });
    if (req.dateOfBirth) p.age = ageFrom(req.dateOfBirth);
    touch(p);
    return delay({ ...p });
  },

  archive(id: string): Promise<void> {
    find(id).status = "ARCHIVED";
    return delay(undefined);
  },
  restore(id: string): Promise<void> {
    find(id).status = "ACTIVE";
    return delay(undefined);
  },
  markDeceased(id: string, deceasedAt: string): Promise<void> {
    const p = find(id);
    p.status = "DECEASED";
    p.deceasedAt = deceasedAt;
    return delay(undefined);
  },
  softDelete(id: string): Promise<void> {
    const idx = PATIENTS.findIndex((x) => x.id === id);
    if (idx >= 0) PATIENTS.splice(idx, 1);
    return delay(undefined);
  },
  remove(id: string): Promise<void> {
    const idx = PATIENTS.findIndex((x) => x.id === id);
    if (idx >= 0) PATIENTS.splice(idx, 1);
    return delay(undefined);
  },

  // ---- allergies ----
  /**
   * #18859 — the mock mirrors the server's conservative rule closely enough to demo: accent- and
   * case-insensitive, whole-word, DRUG + active only, no drug-class inference.
   */
  /**
   * #19184 — the visit-level question. Reuses the per-medicine matcher so the mock cannot drift
   * from itself; like checkDrugAllergy it has no override store, so nothing reads as answered.
   */
  async encounterAllergyConflicts(patientId: string, _encounterId: string, medicineIds: string[]) {
    const out = [];
    for (const medicineId of medicineIds) {
      for (const w of await this.checkDrugAllergy(patientId, medicineId)) {
        out.push({ ...w, medicineId, medicineName: medicineId });
      }
    }
    return out;
  },
  async checkDrugAllergy(patientId: string, medicineId: string, _encounterId?: string) {
    const norm = (v: string | null | undefined) =>
      (v ?? "")
        .normalize("NFD")
        .replace(/\p{M}+/gu, "")
        .replace(/đ/g, "d")
        .toLowerCase();
    const list = (ALLERGIES[patientId] ?? []).filter(
      (a) => a.active && a.category === "DRUG"
    );
    const hay = norm(medicineId);
    return list
      .filter((a) => {
        const n = norm(a.substance);
        return n.length >= 3 && new RegExp(`(^|[^\\p{L}\\d])${n}($|[^\\p{L}\\d])`, "u").test(hay);
      })
      .map((a) => ({
        allergyId: a.id,
        substance: a.substance,
        severity: a.severity,
        reaction: a.reaction ?? null,
        matchedOn: "NAME" as const,
        severe: a.severity === "SEVERE" || a.severity === "ANAPHYLAXIS",
        // The mock never suppresses: it has no override store, and a mock that claimed a warning
        // was already answered would hide the very dialog the mock exists to exercise.
        overridden: false,
      }));
  },
  async overrideDrugAllergy(
    _patientId: string,
    _body: {
      medicineId: string;
      allergyId: string;
      encounterId?: string | null;
      prescriptionId?: string | null;
      reasonCode: AllergyOverrideReason;
      reasonText?: string | null;
      warnedAt?: string | null;
    }
  ): Promise<void> {
    return undefined;
  },

  listAllergies(patientId: string, includeInactive = false): Promise<AllergyResponse[]> {
    const rows = ALLERGIES[patientId] ?? [];
    return delay(includeInactive ? rows : rows.filter((a) => a.active));
  },
  createAllergy(patientId: string, req: AllergyCreateRequest): Promise<void> {
    const now = new Date().toISOString();
    (ALLERGIES[patientId] ??= []).push({
      id: `alg-${Math.random().toString(36).slice(2, 8)}`,
      category: req.category,
      substance: req.substance,
      substanceCode: req.substanceCode ?? null,
      severity: req.severity,
      reaction: req.reaction ?? null,
      onsetDate: req.onsetDate ?? null,
      verifiedAt: null,
      verifiedBy: null,
      source: req.source ?? "PATIENT_REPORTED",
      active: true,
      createdAt: now,
      updatedAt: now,
    });
    return delay(undefined);
  },
  updateAllergy(
    patientId: string,
    allergyId: string,
    req: AllergyUpdateRequest
  ): Promise<void> {
    const a = (ALLERGIES[patientId] ?? []).find((x) => x.id === allergyId);
    if (a) {
      Object.assign(a, req, { updatedAt: new Date().toISOString() });
    }
    return delay(undefined);
  },
  deleteAllergy(patientId: string, allergyId: string): Promise<void> {
    ALLERGIES[patientId] = (ALLERGIES[patientId] ?? []).filter(
      (x) => x.id !== allergyId
    );
    return delay(undefined);
  },

  // ---- conditions ----
  listConditions(patientId: string, includeInactive = false): Promise<ConditionResponse[]> {
    const rows = CONDITIONS[patientId] ?? [];
    return delay(includeInactive ? rows : rows.filter((c) => c.active));
  },
  createCondition(patientId: string, req: ConditionCreateRequest): Promise<void> {
    const now = new Date().toISOString();
    (CONDITIONS[patientId] ??= []).push({
      id: `cnd-${Math.random().toString(36).slice(2, 8)}`,
      icd10Code: req.icd10Code ?? null,
      nameVi: req.nameVi,
      onsetDate: req.onsetDate ?? null,
      resolvedDate: null,
      severity: req.severity ?? null,
      notes: req.notes ?? null,
      active: true,
      createdAt: now,
      updatedAt: now,
    });
    return delay(undefined);
  },
  updateCondition(
    patientId: string,
    conditionId: string,
    req: ConditionUpdateRequest
  ): Promise<void> {
    const c = (CONDITIONS[patientId] ?? []).find((x) => x.id === conditionId);
    if (c) {
      Object.assign(c, req, { updatedAt: new Date().toISOString() });
    }
    return delay(undefined);
  },
  deleteCondition(patientId: string, conditionId: string): Promise<void> {
    CONDITIONS[patientId] = (CONDITIONS[patientId] ?? []).filter(
      (x) => x.id !== conditionId
    );
    return delay(undefined);
  },

  // ---- identifiers (CCCD history + BHYT) ----
  listIdentifiers(patientId: string): Promise<PatientIdentifierResponse[]> {
    return delay(IDENTIFIERS[patientId] ?? []);
  },
  addCccd(
    patientId: string,
    req: { value: string; issuedBy?: string | null; issuedAt?: string | null }
  ): Promise<void> {
    const list = (IDENTIFIERS[patientId] ??= []);
    list.forEach((i) => {
      if (i.identifierType === "CCCD") i.isPrimary = false;
    });
    list.push({
      id: `idf-${Math.random().toString(36).slice(2, 8)}`,
      identifierType: "CCCD",
      value: req.value,
      issuedBy: req.issuedBy ?? null,
      issuedAt: req.issuedAt ?? null,
      isPrimary: true,
      createdAt: new Date().toISOString(),
    });
    return delay(undefined);
  },
  setBhyt(patientId: string, value: string): Promise<void> {
    const list = (IDENTIFIERS[patientId] ??= []);
    const bhyt = list.find((i) => i.identifierType === "BHYT");
    if (!value.trim()) {
      IDENTIFIERS[patientId] = list.filter((i) => i.identifierType !== "BHYT");
    } else if (bhyt) {
      bhyt.value = value;
    } else {
      list.push({
        id: `idf-${Math.random().toString(36).slice(2, 8)}`,
        identifierType: "BHYT",
        value,
        issuedBy: null,
        issuedAt: null,
        isPrimary: false,
        createdAt: new Date().toISOString(),
      });
    }
    return delay(undefined);
  },

  // ---- alerts ----
  listAlerts(patientId: string): Promise<AlertResponse[]> {
    return delay((ALERTS[patientId] ?? []).filter((a) => a.active));
  },
  createAlert(patientId: string, req: AlertCreateRequest): Promise<void> {
    (ALERTS[patientId] ??= []).push({
      id: `alt-${Math.random().toString(36).slice(2, 8)}`,
      alertType: req.alertType,
      severity: req.severity,
      titleVi: req.titleVi,
      descriptionVi: req.descriptionVi ?? null,
      expiresAt: req.expiresAt ?? null,
      acknowledgedBy: null,
      acknowledgedAt: null,
      active: true,
      createdAt: new Date().toISOString(),
    });
    return delay(undefined);
  },
  acknowledgeAlert(patientId: string, alertId: string): Promise<void> {
    const a = (ALERTS[patientId] ?? []).find((x) => x.id === alertId);
    if (a) {
      a.acknowledgedAt = new Date().toISOString();
      a.acknowledgedBy = "BS. Nguyễn Văn A";
    }
    return delay(undefined);
  },
  dismissAlert(patientId: string, alertId: string): Promise<void> {
    const a = (ALERTS[patientId] ?? []).find((x) => x.id === alertId);
    if (a) a.active = false;
    return delay(undefined);
  },

  // ---- tags ----
  listTags(patientId: string): Promise<TagsResponse> {
    return delay({ tags: TAGS[patientId] ?? [] });
  },
  replaceTags(patientId: string, tags: string[]): Promise<TagsResponse> {
    TAGS[patientId] = [...tags];
    return delay({ tags: [...tags] });
  },
};
